import React, { useContext, useEffect, useState } from 'react';
import { Typography, Grid, TextField, Hidden } from '@material-ui/core';
import { useForm, Controller } from 'react-hook-form';
import { useMsal } from '@azure/msal-react';
import Button from '../../elements/Button';
import { ClientConfigContext } from '../../config/clientConfig';
import { AccountInfo } from '@azure/msal-common';
import { PatientAdminServiceIdentity, SearchResult } from '../../types/api';
import { auditPageView, searchAccounts } from './api';
import MultipleAccountsDisplayComponent from './components/MultipleAccountsDisplayComponent';
import SearchResultDataComponent from './components/SearchResultDataComponent';
import Banner from './components/Banner';
import { getDisplayUserName } from '../../utils/AccountDetailsUtil';
import { SessionContext } from '../../session/sessionContext';
import { mapSearchResults } from './convert';
import { SearchOptions } from '../../types/front-end';

function SearchPage() {
    const msalContext = useMsal();
    const { instance } = msalContext;
    const accounts = msalContext.accounts as AccountInfo[];
    const clientConfiguration = useContext(ClientConfigContext);
    const sessionId = useContext(SessionContext);
    const {
        handleSubmit,
        control,
        formState: { errors, isDirty, isValid },
        setValue,
    } = useForm<{ accountId: string }>({ mode: 'onChange' });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Controls whether the SearchResults are displayed as an in-depth single-account view.
    // The other display mode are the accounts are shown as cards stacked side-by-side.
    const [isSingleAccountView, setIsSingleAccountView] = useState(false);

    // TODO: It appears that these two variables holds the same data. They are for different views.
    const [searchDisplayData, setSearchDisplayData] = useState<SearchResult[]>();
    const [multipleData, setMultipleData] = useState<SearchResult[]>();

    const logout = () => {
        // The account's ID Token must contain the login_hint optional claim to avoid the account picker
        instance.logoutRedirect({ account: instance.getAllAccounts()[0] });
    };

    /**
     * The click event handler for the Search Button.
     * @param searchTerm email, mrn, or epi
     */
    const onSearch = async (searchTerm: string): Promise<void> => {
        setSearchTerm(searchTerm);
        searchMultiAccounts({ searchTerm: searchTerm, refreshCorrelationId: true }); // runs search
    };

    /**
     * Search for an account. In effect, this refreshes most of the page.
     *
     * This is also invoked by child components to refine a search by region.
     * @param accountId - the search term. Either email, mrn, or epi
     * @param region
     * @param healthSystemId
     */
    const searchMultiAccounts = async ({ searchTerm, refreshCorrelationId, region, healthSystemId }: SearchOptions) => {
        try {
            if (region && healthSystemId && searchTerm !== searchTerm) {
                //After the update Email flow, if  link record is tried,
                //update the search Textbox value with the new email
                setValue('accountId', 'Updating search...');
            }
            setIsSingleAccountView(region ? true : false);
            setMultipleData(undefined);
            setLoading(true);
            const data = await searchAccounts(searchTerm, '', instance, accounts, clientConfiguration, sessionId, refreshCorrelationId);
            const patientDtos: PatientAdminServiceIdentity[] = data.patientAdminServiceIdentityDtos;
            const searchResults = mapSearchResults(patientDtos, data.inputSearchType);
            if (searchResults) {
                if (region && healthSystemId) {
                    const selectedItem = searchResults.filter((x) => x.healthSystemId === healthSystemId);
                    setSearchDisplayData(selectedItem?.length > 0 ? selectedItem : searchResults);
                    setSearchTerm(searchTerm);
                    setValue('accountId', searchTerm);
                }
                setMultipleData(searchResults);
                setLoading(false);
            }
        } catch (err: any) {
            setLoading(false);
            /**
             * This is intended to capture and log errors, if it not exists
             * we could be blind to errors that are happening.
             */
            console.error(err);
        }
    };

    useEffect(() => {
        auditPageView(instance, accounts, clientConfiguration, sessionId);
    }, []);

    /**
     * This callback is invoked by MultipleAccountsDisplayComponent. The user selects a
     * @param searchResult
     */
    const showSearchData = (searchResult: SearchResult[]) => {
        setSearchDisplayData(searchResult);
        setIsSingleAccountView(true);
    };

    const getDisplayScreen = () => {
        if (loading) {
            return (
                <>
                    <Grid container item xs alignItems='center' className='searchOutputBodyPlaceholder'>
                        <Grid item xs>
                            <Typography variant='body1'>Loading...</Typography>
                        </Grid>
                    </Grid>
                </>
            );
        } else {
            if (isSingleAccountView && searchDisplayData) {
                return (
                    <>
                        <SearchResultDataComponent
                            searchDisplayData={searchDisplayData}
                            searchTerm={searchTerm}
                            instance={instance}
                            accounts={accounts}
                            searchAccountsCallback={searchMultiAccounts}
                        />
                    </>
                );
            } else {
                return (
                    <>
                        <MultipleAccountsDisplayComponent searchTerm={searchTerm} multipleSearchResults={multipleData} showSearchData={showSearchData} />
                    </>
                );
            }
        }
    };

    return (
        <>
            <Grid container className='searchBoundingBox'>
                <Grid container item md={12} sm={12} xs={12} className='searchTitle'>
                    <Banner />
                    <Grid container alignItems='center' item md={7} sm={6} xs={4} className='searchPageLogoutSection'>
                        <Hidden smDown>
                            <Typography variant='h6' className='searchPageUsername'>
                                {getDisplayUserName(accounts[0], true)}
                            </Typography>
                        </Hidden>
                        <Hidden mdUp>
                            <Typography variant='h6' className='searchPageUsername'>
                                {getDisplayUserName(accounts[0], false)}
                            </Typography>
                        </Hidden>
                        <Button type='button' variant='outlined' color='secondary' fontSize='14px' onClick={logout}>
                            SIGN-OUT
                        </Button>
                    </Grid>
                </Grid>
                <form onSubmit={handleSubmit(({ accountId }) => onSearch(accountId))} className='searchForm'>
                    <Grid container item md={12} sm={12} xs={12} direction='row' spacing={2} alignItems='center'>
                        <Grid item className='searchBoxLabel'>
                            <Typography variant='h4'>Account Search:</Typography>
                        </Grid>
                        <Grid item md={8} sm={8} xs={8}>
                            <Controller
                                name='accountId'
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        className='searchBoxInput'
                                        variant='outlined'
                                        id='accountId'
                                        autoComplete='off'
                                        fullWidth
                                        error={errors.accountId?.type === 'required'}
                                        helperText={errors.accountId?.type === 'required' && 'Email, username, MRN, or EPI is required'}
                                        placeholder='Account email, MRN, or EPI'
                                        InputLabelProps={{
                                            shrink: true,
                                            error: false,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid container alignItems='center' item md={2} sm={2} xs={2}>
                            <Button disabled={!isDirty || !isValid} type='submit' variant='contained' color='primary' fontSize='16px'>
                                Search
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Grid>
            <Grid container alignItems='flex-start' direction='column' className='ml20'>
                {getDisplayScreen()}
            </Grid>
        </>
    );
}

export default SearchPage;
